import React, { memo } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    makeStyles,
    Theme,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'

interface DeleteConfirmDialogProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    documentName: string
    isDeleting?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        maxWidth: 400,
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    warningIcon: {
        color: theme.palette.error.main,
    },
    content: {
        paddingTop: theme.spacing(1),
    },
    documentName: {
        fontWeight: 600,
    },
    actions: {
        padding: theme.spacing(2, 3),
    },
    deleteButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
}))

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = memo(
    ({ isOpen, onClose, onConfirm, documentName, isDeleting = false }) => {
        const classes = useStyles()

        return (
            <Dialog
                open={isOpen}
                onClose={onClose}
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle disableTypography>
                    <div className={classes.titleContainer}>
                        <WarningIcon className={classes.warningIcon} />
                        <span>Delete Document</span>
                    </div>
                </DialogTitle>
                <DialogContent className={classes.content}>
                    <DialogContentText>
                        Are you sure you want to delete{' '}
                        <span className={classes.documentName}>
                            &ldquo;{documentName}&rdquo;
                        </span>
                        ? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <Button
                        onClick={onClose}
                        color="default"
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className={classes.deleteButton}
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    },
)
